/**
 * Единственный источник визуальных решений для графиков по узлам.
 *
 * До этого модуля каждый компонент объявлял свою палитру, свой тултип и свою формулу
 * высоты, поэтому карточки расходились по виду, а «радужная» раскраска попадала даже
 * на двухзначные булевы графики, где второй цвет ничего не значит. Здесь цвета, подписи,
 * оси и высоты заданы один раз, а рендереры их только потребляют — консистентность
 * становится структурной, а не вопросом дисциплины при добавлении нового типа графика.
 */

/**
 * Категориальная палитра — подмножество палитры «Аналитики сотрудников», чтобы разделы
 * читались как одна система.
 *
 * Красный и зелёный из неё исключены намеренно: они заняты семантикой (отрицательный и
 * положительный исход). Пока они были в общем наборе, распределение цветов по хешу кода
 * выдавало красный нейтральной категории — «Ожидается конкретное действие» выглядело
 * как проблема, хотя это самый обычный исход. Ровно восемь цветов — столько же, сколько
 * секторов максимум держит кольцо.
 */
export const CATEGORICAL_PALETTE = [
  '#2563eb',
  '#db2777',
  '#f97316',
  '#7c3aed',
  '#0ea5e9',
  '#facc15',
  '#14b8a6',
  '#64748b'
];

/** Один цвет на весь график: когда столбцы — это одна метрика по разным категориям. */
export const SEQUENTIAL_COLOR = '#60a5fa';
export const PRIMARY_COLOR = '#2563eb';

export const SEMANTIC_COLORS = {
  positive: '#16a34a',
  negative: '#ef4444',
  warning: '#f59e0b',
  neutral: '#94a3b8',
  progress: '#2563eb'
};

/**
 * Коды, у которых цвет обязан быть семантическим, а не случайным из палитры:
 * «Да/Нет» зелёным/красным читается без легенды, а «не определено» не должно
 * выглядеть как полноценная категория.
 */
const CODE_ROLES = {
  true: 'positive',
  false: 'negative',
  unknown: 'neutral',
  '—': 'neutral',
  __other: 'neutral'
};

/** FNV-1a: нужен только детерминизм и равномерность, не криптостойкость. */
const hashCode = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
};

/**
 * Цвет закрепляется за кодом значения, а не за его позицией в ответе. Позиция меняется
 * при каждом обновлении (сортировка по убыванию), и категория перекрашивалась бы на
 * глазах у пользователя, а в двух разных графиках один и тот же код был бы разным цветом.
 *
 * `usedColors` разрешает коллизии хеша внутри одной карточки: два кода, попавшие в один
 * слот палитры, разъезжаются по соседним, иначе соседние сектора donut сливаются.
 */
export const resolveCodeColor = (code, index, options = {}) => {
  const { tone = 'categorical', colorRoles = {}, usedColors } = options;
  const key = String(code ?? '');

  const role = colorRoles[key] ?? CODE_ROLES[key];
  if (role && SEMANTIC_COLORS[role]) {
    return SEMANTIC_COLORS[role];
  }

  if (tone === 'sequential') {
    return SEQUENTIAL_COLOR;
  }

  if (tone === 'progress') {
    return SEMANTIC_COLORS.progress;
  }

  let slot = key ? hashCode(key) % CATEGORICAL_PALETTE.length : index % CATEGORICAL_PALETTE.length;

  if (usedColors) {
    for (let attempt = 0; attempt < CATEGORICAL_PALETTE.length; attempt += 1) {
      const candidate = (slot + attempt) % CATEGORICAL_PALETTE.length;
      if (!usedColors.has(candidate)) {
        usedColors.add(candidate);
        slot = candidate;
        break;
      }
    }
  }

  return CATEGORICAL_PALETTE[slot];
};

/**
 * Пятиступенчатая рампа для тепловых карт и оценок. Пороги те же, что были в legacy
 * `ChecklistQualityBlock`. Таблица «Эффективность операторов» этой рампой больше не красит:
 * цвет там означает место среди сотрудников (`employeeEfficiencyHighlight.js`), а не уровень.
 *
 * Принимает проценты 0..100. Оценки узлов приходят в 0..1, их нормализует вызывающий:
 * прогнать 0.67 через эту функцию значит покрасить хорошую оценку в красный.
 */
export const resolveRampClass = (percent) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) {
    return 'bg-muted text-muted-foreground';
  }

  const rate = Math.max(0, Math.min(100, percent));

  if (rate < 20) return 'bg-red-100 text-red-800';
  if (rate < 40) return 'bg-orange-100 text-orange-800';
  if (rate < 60) return 'bg-yellow-100 text-yellow-800';
  if (rate < 80) return 'bg-lime-100 text-lime-800';
  return 'bg-green-100 text-green-800';
};

export const formatCalls = (value) => (Number(value) || 0).toLocaleString('ru-RU');

export const formatShare = (value) => `${(Number(value) || 0).toFixed(1)}%`;

/**
 * Подпись у столбца: «124 (18.3%)» — ровно как на демо-стенде.
 *
 * `compact` — телефон: полная подпись занимает ~80px из 250px ширины графика, и на столбцы
 * не остаётся места. Доля при этом не теряется — она есть в тултипе.
 */
export const formatValueLabel = (calls, share, { compact = false } = {}) =>
  compact ? formatCalls(calls) : `${formatCalls(calls)} (${formatShare(share)})`;

/**
 * Подписи значений рисуем сами: нужно «N (x%)» и разное позиционирование для
 * горизонтальных и вертикальных столбцов.
 *
 * Печатается ровно то, что пришло в `value`, то есть готовая строка из поля данных.
 * Собирать подпись здесь по строке данных нельзя: `LabelList` передаёт в `content`
 * только геометрию, `value` и `index`, но не саму строку — попытка прочитать
 * `payload.share` давала «0 (0.0%)» на всех столбцах.
 */
export const createValueLabelRenderer = ({ orientation = 'horizontal' } = {}) => {
  const render = ({ x, y, width, height, value }) => {
    const text = value === null || value === undefined ? '' : String(value);
    if (!text) {
      return null;
    }

    const isHorizontal = orientation === 'horizontal';
    const textX = isHorizontal ? (x ?? 0) + (width ?? 0) + 6 : (x ?? 0) + (width ?? 0) / 2;
    const textY = isHorizontal ? (y ?? 0) + (height ?? 0) / 2 : (y ?? 0) - 6;

    return (
      <text
        x={textX}
        y={textY}
        textAnchor={isHorizontal ? 'start' : 'middle'}
        dominantBaseline={isHorizontal ? 'middle' : 'auto'}
        fill="hsl(var(--foreground))"
        fontSize="11"
        fontWeight="600"
      >
        {text}
      </text>
    );
  };

  return render;
};

export const AXIS = {
  tick: { fontSize: 11 },
  grid: { strokeDasharray: '4 4', stroke: 'rgba(148,163,184,0.25)' },
  tooltipWrapperStyle: { zIndex: 30 },
  maxBarSize: 26,
  radiusHorizontal: [0, 4, 4, 0],
  radiusVertical: [4, 4, 0, 0],
  /** Место справа под подписи «124 (18.3%)»: без него длинная подпись уезжает за карточку. */
  labelGutter: 96,
  /** То же на телефоне, где подпись сокращена до числа (`formatValueLabel` с `compact`). */
  labelGutterCompact: 40
};

/**
 * Подписи категорий на телефоне: не больше двух строк, дальше многоточие. Recharts переносит
 * текст тика по ширине оси, и без ограничения длинная формулировка разрасталась на четыре-пять
 * строк и наезжала на соседние столбцы. Полный текст остаётся в тултипе.
 */
export const COMPACT_CATEGORY_TICK = { ...AXIS.tick, maxLines: 2 };

/** Класс области графика — одинаковая подложка у всех типов. */
export const PLOT_AREA_CLASS = 'relative w-full min-w-0 flex-1 rounded-md bg-muted/30 px-2 py-4';

/** Фиксированные высоты вместо «сколько строк — столько пикселей» у каждого компонента. */
export const CHART_HEIGHT = { sm: 200, md: 264, lg: 320 };


const AXIS_WIDTH_MIN = 110;
const AXIS_WIDTH_MAX = 230;
const AXIS_CHAR_WIDTH = 6.6;
const COMPACT_AXIS_SHARE = 0.4;
const COMPACT_AXIS_MIN = 72;

/**
 * Ширина колонки подписей категорий. Подписи приходят из пакета и бывают и «Да», и целым
 * предложением, поэтому колонку считаем по самой длинной подписи и зажимаем в границы:
 * фиксированные 190px либо обрезали длинные названия, либо съедали половину узкой карточки.
 *
 * `compact` + `containerWidth` — телефон. Там график шириной ~250px, и даже нижняя граница
 * в 110px вместе с отступом под подписи значений оставляла столбцам несколько десятков
 * пикселей. Поэтому колонка не больше доли ширины графика; подписи при этом переносятся
 * в две строки (`COMPACT_CATEGORY_TICK`). Без `compact` ширина контейнера не учитывается:
 * на десктопе колонка обязана остаться прежней.
 */
export const resolveAxisWidth = (rows, { containerWidth, compact = false } = {}) => {
  const longest = rows.reduce((max, row) => Math.max(max, (row.label ?? '').length), 0);
  const width = Math.min(AXIS_WIDTH_MAX, Math.max(AXIS_WIDTH_MIN, Math.round(longest * AXIS_CHAR_WIDTH) + 16));

  if (!compact || !containerWidth) {
    return width;
  }

  return Math.min(width, Math.max(COMPACT_AXIS_MIN, Math.round(containerWidth * COMPACT_AXIS_SHARE)));
};

export const OTHER_CODE = '__other';

/**
 * Приводит строки ответа к тому виду, в котором их ждёт рендерер: сортировка, top-N со
 * сворачиванием хвоста и — по требованию — доборка объявленных, но не встретившихся кодов.
 *
 * `fillDeclared` разделяет два разных случая при одном и том же `sort: declared`.
 * Воронке добор обязателен: пропущенный этап рвёт цепочку конверсии и читается как
 * «такого не бывает». Чек-листу он вреден: пункт, который ни разу не оценивался, дал бы
 * строку из прочерков и нулей, то есть шум вместо информации.
 */
export const applyValueShaping = (rows = [], spec = {}, declaredOptions = []) => {
  const { sort = 'value_desc', top = null, otherBucket = false, fillDeclared = false } = spec;

  let shaped = rows.map((row) => ({ ...row }));

  if (sort === 'declared' && declaredOptions.length > 0) {
    const byCode = new Map(shaped.map((row) => [row.code, row]));

    shaped = declaredOptions
      .map(
        (option) =>
          byCode.get(option.code) ??
          (fillDeclared
            ? { code: option.code, label: option.label || option.code, calls: 0, share: 0 }
            : null)
      )
      .filter(Boolean);
  } else if (sort === 'label_asc') {
    shaped.sort((left, right) => (left.label ?? '').localeCompare(right.label ?? '', 'ru'));
  } else if (sort !== 'none') {
    shaped.sort((left, right) => right.calls - left.calls || (left.label ?? '').localeCompare(right.label ?? '', 'ru'));
  }

  if (top && shaped.length > top) {
    const head = shaped.slice(0, top);

    if (otherBucket) {
      const tail = shaped.slice(top);
      head.push({
        code: OTHER_CODE,
        label: 'Прочее',
        calls: tail.reduce((sum, row) => sum + row.calls, 0),
        share: tail.reduce((sum, row) => sum + row.share, 0)
      });
    }

    return head;
  }

  return shaped;
};
